import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Loader2, ShieldAlert, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAssistant } from "@/context/AssistantContext";

const RISK = { lav: null, middels: "Endrer status/plan – sjekk detaljene", hoy: "Høy konsekvens – konflikter eller flere moduler berøres" };

/**
 * A proposal from the assistant: what will happen, exactly, with one «Bekreft». Nothing is written until then.
 * Confirmed cards keep the result (deep-link) so the conversation stays a log of what was actually done.
 */
export const ActionCard = ({ action }) => {
  const navigate = useNavigate();
  const { confirmAction, rejectAction } = useAssistant();
  const [showAll, setShowAll] = useState(false);
  const a = action;
  const status = a.status || "proposed";
  const rows = showAll ? a.preview : a.preview?.slice(0, 5);

  const confirm = async () => {
    try {
      const r = await confirmAction(a.id);
      toast.success(a.title, { description: r?.result?.label });
    } catch (e) {
      toast.error("Kunne ikke utføre", { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl ring-1 transition-colors duration-200",
        status === "confirmed" ? "bg-emerald-500/[0.06] ring-emerald-500/20" : status === "rejected" ? "bg-muted/40 ring-black/[0.04] dark:ring-white/5" : status === "failed" ? "bg-rose-500/[0.06] ring-rose-500/20" : "bg-card ring-primary/25 shadow-[0_8px_24px_-16px_rgba(38,112,242,0.45)]"
      )}
      data-testid={`ai-action-${a.type}`}
      data-status={status}
    >
      <div className="flex items-start gap-3 px-4 pt-3.5">
        <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", status === "confirmed" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : status === "failed" ? "bg-rose-500/15 text-rose-600" : "bg-primary/10 text-primary")} aria-hidden>
          {status === "confirmed" ? <Check className="h-4 w-4" strokeWidth={2} /> : <Sparkles className="h-4 w-4" strokeWidth={1.75} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[13px] font-semibold leading-5">{a.title}</span>
            <span className="text-[11.5px] text-muted-foreground">{status === "proposed" ? "Utkast – ingenting er gjort ennå" : status === "confirmed" ? "Utført" : status === "rejected" ? "Avvist" : "Feilet"}</span>
          </div>
          <div className="truncate text-[12.5px] leading-5 text-foreground/80">{a.summary}</div>
        </div>
      </div>
      {rows?.length > 0 && (
        <dl className="mx-4 mt-3 grid grid-cols-[minmax(80px,auto)_1fr] gap-x-4 gap-y-1 text-[12px] leading-5">
          {rows.map((p, i) => (
            <div key={i} className="contents">
              <dt className="text-muted-foreground">{p.label}</dt>
              <dd className="min-w-0 break-words">{String(p.value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {a.preview?.length > 5 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mx-4 mt-1 text-[11.5px] text-muted-foreground hover:text-foreground">
          {showAll ? "Vis færre" : `+${a.preview.length - 5} felt`}
        </button>
      )}
      {RISK[a.risk] && status === "proposed" && (
        <div className="mx-4 mt-3 flex items-center gap-1.5 text-[11.5px] text-amber-700 dark:text-amber-300">
          <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.75} /> {RISK[a.risk]}
        </div>
      )}
      {a.error && <div className="mx-4 mt-3 text-[12px] text-rose-600">{String(a.error)}</div>}
      <div className="mt-3 flex items-center gap-2 border-t border-border/60 px-4 py-2.5 dark:border-white/5">
        {status === "proposed" && a.allowed !== false && (
          <>
            <Button size="sm" onClick={confirm} disabled={a.busy} data-testid="ai-action-confirm">
              {a.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Bekreft
            </Button>
            <Button size="sm" variant="ghost" onClick={() => rejectAction(a.id)} disabled={a.busy} data-testid="ai-action-reject">
              <X className="h-4 w-4" /> Avvis
            </Button>
          </>
        )}
        {status === "proposed" && a.allowed === false && <span className="text-[12px] text-muted-foreground">Rollen din har ikke tilgang til å utføre dette ({a.permission}).</span>}
        {status === "confirmed" && a.result?.url && (
          <button type="button" onClick={() => navigate(a.result.url)} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-emerald-700 hover:underline dark:text-emerald-300" data-testid="ai-action-open-result">
            {a.result.label || "Åpne"} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground">Logges i audit</span>
      </div>
    </div>
  );
};
